import {bootTrace, isBootTraceEnabled} from '../debug/bootTrace';

/**
 * Управление громкостью HTML-аудио, работающее в том числе на iOS.
 *
 * В WebKit на iPhone/iPad громкость элемента <audio> не подчиняется странице:
 * она отдана физическим кнопкам устройства, и присваивание element.volume
 * либо игнорируется, либо запоминается в свойстве, но ни на что не влияет.
 * Из-за этого слайдеры громкости звуков и музыки там ни на что не влияли.
 *
 * Единственный работающий путь — пропустить элемент через Web Audio и
 * управлять громкостью узла GainNode: именно так регулируются игровые звуки
 * через Phaser, и на iOS они регулируются корректно.
 *
 * Определять платформу пробой (присвоить volume и прочитать обратно) оказалось
 * нельзя: на iOS свойство возвращает присвоенное значение, хотя на звук оно не
 * действует. Поэтому развилки по платформе нет вовсе — граф строится везде,
 * где доступен проснувшийся AudioContext. Прямое присваивание остается лишь
 * запасным путем, пока контекст спит.
 */

const RESUME_GESTURE_EVENTS = ['pointerup', 'touchend', 'keydown'];

let audioContext = null;
let isResumeListenerAttached = false;
let isFirstGraphTraced = false;
let isGraphFailureTraced = false;

// Узлы графа на элемент: и gain (крутим громкость), и source (нужен, чтобы
// отключить его при освобождении одноразовых клонов)
const audioNodesByElement = new WeakMap();

const resumeAudioContext = () => {
  if (audioContext && audioContext.state === 'suspended') {
    audioContext.resume().catch(() => {
    });
  }
};

const getAudioContext = () => {
  if (audioContext) {
    resumeAudioContext();
    return audioContext;
  }

  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) {
    bootTrace('audio: no AudioContext');
    return null;
  }

  try {
    audioContext = new AudioContextClass();
  } catch (error) {
    bootTrace(`audio: ctx failed ${error.message}`);
    return null;
  }

  // Контекст стартует приостановленным и оживает только в обработчике жеста,
  // а на iOS засыпает снова после сворачивания приложения — поэтому слушатели
  // не снимаем. На тачскрине жестом считается pointerup/touchend, но не
  // pointerdown: он засчитывается только для мыши
  if (!isResumeListenerAttached) {
    isResumeListenerAttached = true;
    RESUME_GESTURE_EVENTS.forEach((eventName) =>
      document.addEventListener(eventName, resumeAudioContext));
  }

  resumeAudioContext();
  return audioContext;
};

const getAudioNodes = (audio) => {
  const existingNodes = audioNodesByElement.get(audio);
  if (existingNodes) return existingNodes;

  const context = getAudioContext();
  if (!context) return null;

  // Пока контекст спит, граф не строим: с момента createMediaElementSource
  // звук элемента идет ТОЛЬКО через граф, и на спящем контексте это дало бы
  // тишину вместо всего лишь нерегулируемой громкости. Разбудить контекст
  // может только жест пользователя — до тех пор работает запасной путь
  if (context.state !== 'running') return null;

  try {
    // createMediaElementSource можно вызвать для элемента лишь однажды —
    // отсюда и кеш, и обязательное подключение к destination
    const source = context.createMediaElementSource(audio);
    const gainNode = context.createGain();
    source.connect(gainNode);
    gainNode.connect(context.destination);

    // С этого момента громкостью управляет только gain. Но свойство volume
    // элемента действует и дальше: звук умножается на него еще ДО графа
    // (проверено в Chromium). Если до появления графа громкость ставилась
    // запасным путем, она осталась в свойстве — и итог был бы произведением
    // двух громкостей, а после нуля — тишиной при любом gain: музыка, впервые
    // включенная по клику, не звучала вовсе. Элементу возвращаем единицу;
    // нужную громкость вызывающий код тут же выставит в gain
    audio.volume = 1;

    const nodes = {source, gainNode};
    audioNodesByElement.set(audio, nodes);

    if (!isFirstGraphTraced) {
      isFirstGraphTraced = true;
      bootTrace('audio: gain graph ok');
    }
    return nodes;
  } catch (error) {
    if (!isGraphFailureTraced) {
      isGraphFailureTraced = true;
      bootTrace(`audio: graph failed ${error.message}`);
    }
    return null;
  }
};

/**
 * Ставит громкость элемента в диапазоне 0..1
 */
export const setAudioElementVolume = (audio, volume) => {
  if (!audio) return;
  const clampedVolume = Math.max(0, Math.min(1, volume));

  const nodes = getAudioNodes(audio);
  if (nodes) {
    nodes.gainNode.gain.value = clampedVolume;
    return;
  }

  // Графа еще нет — прямое присваивание: на большинстве платформ это рабочий
  // путь, на iOS оно просто ни на что не повлияет
  audio.volume = clampedVolume;
};

/**
 * Текущая громкость элемента. Отдельная функция нужна, потому что у элемента,
 * заведенного в граф, действует громкость gain-узла, а не element.volume —
 * плавные затухания, стартующие от текущего значения, иначе прыгали бы
 */
export const getAudioElementVolume = (audio) => {
  if (!audio) return 0;
  const nodes = audioNodesByElement.get(audio);
  return nodes ? nodes.gainNode.gain.value : audio.volume;
};

/**
 * Отпускает узлы графа. Обязательна для одноразовых элементов (клоны звуков в
 * MenuSoundManager): узлы держат ссылку на элемент и остаются подключенными к
 * выходу, пока их явно не отключить
 */
export const releaseAudioElementVolume = (audio) => {
  if (!audio) return;
  const nodes = audioNodesByElement.get(audio);
  if (!nodes) return;

  try {
    nodes.source.disconnect();
    nodes.gainNode.disconnect();
  } catch (error) {
    // Узлы уже отключены — ничего делать не нужно
  }
  audioNodesByElement.delete(audio);
};

/**
 * Разовая диагностика для трассировки (?bootTrace=1): слушается ли на этой
 * платформе присваивание element.volume и в каком состоянии аудиоконтекст.
 * Именно проба на volume ввела в заблуждение при первой попытке починки —
 * пусть ее результат будет виден прямо на устройстве
 */
export const traceAudioVolumeSupport = () => {
  // Без включенной трассировки не трогаем ничего: getAudioContext ниже создал
  // бы аудиоконтекст раньше, чем он реально понадобился
  if (!isBootTraceEnabled()) return;

  let isVolumeSettable;
  try {
    const probe = new Audio();
    probe.volume = 0.5;
    isVolumeSettable = probe.volume === 0.5 ? 'yes' : 'no';
  } catch (error) {
    isVolumeSettable = 'error';
  }
  const context = getAudioContext();
  bootTrace(`audio: volume settable=${isVolumeSettable} ctx=${context ? context.state : 'none'}`);
};
